import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const TABS = ['Available Trainings', 'My Enrollments', 'My CPD Records'];

const STATUS_COLORS = {
  upcoming: 'bg-blue-100 text-blue-700',
  ongoing: 'bg-green-100 text-green-700',
  completed: 'bg-purple-100 text-purple-700',
};

const LEVEL_COLORS = {
  beginner: 'bg-emerald-100 text-emerald-700',
  intermediate: 'bg-amber-100 text-amber-700',
  advanced: 'bg-red-100 text-red-700',
};

export default function MCPDLog() {
  const [tab, setTab] = useState('Available Trainings');
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);

  useEffect(() => {
    api.get('/mcpd/my/').then(r => setRecords(r.data.results || r.data))
      .finally(() => setLoadingRecords(false));
  }, []);

  const totalApproved = records.filter(r => r.status === 'approved').reduce((s, r) => s + r.cpd_points, 0);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">MCPD Training & CPD</h1>
        <p className="text-sm text-gray-500">Browse trainings, enroll in bootcamps, and manage your CPD records. Approved CPD: <strong className="text-[#0B5D1E]">{totalApproved} pts</strong></p>
      </div>

      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
              tab === t ? 'bg-white text-[#0B5D1E] shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>{t}</button>
        ))}
      </div>

      {tab === 'Available Trainings' && <AvailableTrainingsTab />}
      {tab === 'My Enrollments' && <MyEnrollmentsTab />}
      {tab === 'My CPD Records' && <MyCPDRecordsTab records={records} setRecords={setRecords} loading={loadingRecords} />}
    </div>
  );
}


// ═══ Available Trainings Tab ═══

function AvailableTrainingsTab() {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewTraining, setViewTraining] = useState(null);
  const [search, setSearch] = useState('');

  const fetchTrainings = useCallback(() => {
    setLoading(true);
    api.get('/mcpd/trainings/')
      .then(r => setTrainings(r.data.results || r.data))
      .catch(() => toast.error('Failed to load trainings.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchTrainings(); }, [fetchTrainings]);

  if (viewTraining) return <TrainingDetailView training={viewTraining} onBack={() => { setViewTraining(null); fetchTrainings(); }} />;

  return (
    <div>
      {/* Search */}
      <div className="relative mb-4">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        <input type="text" placeholder="Search trainings..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent" />
      </div>
      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B5D1E] mx-auto"></div></div>
      ) : trainings.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
          <p className="text-gray-500 font-semibold">No trainings available right now.</p>
          <p className="text-sm text-gray-400 mt-1">Check back later for upcoming bootcamps and workshops.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {trainings.filter(t => !search || t.title.toLowerCase().includes(search.toLowerCase()) || (t.category_display || '').toLowerCase().includes(search.toLowerCase()) || (t.instructor_name || '').toLowerCase().includes(search.toLowerCase())).map((t, idx) => (
            <div key={t.id} className="bg-white rounded-xl shadow-sm border hover:shadow-md transition overflow-hidden cursor-pointer relative" onClick={() => setViewTraining(t)}>
              <div className="absolute top-4 left-4 bg-[#0B5D1E] text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center z-10">{idx + 1}</div>
              <div className={`h-2 ${
                t.category === 'surveying' ? 'bg-blue-500' :
                t.category === 'gis' ? 'bg-teal-500' :
                t.category === 'law' ? 'bg-amber-500' :
                t.category === 'ethics' ? 'bg-purple-500' :
                t.category === 'technology' ? 'bg-indigo-500' :
                t.category === 'field' ? 'bg-green-500' :
                t.category === 'management' ? 'bg-orange-500' : 'bg-gray-400'
              }`} />
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 truncate">{t.title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{t.category_display}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ml-2 whitespace-nowrap ${STATUS_COLORS[t.status] || 'bg-gray-100 text-gray-600'}`}>
                    {t.status_display}
                  </span>
                </div>

                <div className="space-y-2 text-sm text-gray-600 mb-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    <span>{t.date_from} — {t.date_to}</span>
                  </div>
                  {t.instructor_name && (
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                      <span>{t.instructor_name}</span>
                    </div>
                  )}
                  {(t.venue || t.is_virtual) && (
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      <span>{t.is_virtual ? 'Virtual / Online' : t.venue}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 mb-3">
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold ${LEVEL_COLORS[t.level] || ''}`}>{t.level_display}</span>
                  <span className="text-xs text-[#0B5D1E] font-bold">{t.cpd_points} CPD pts</span>
                  {t.max_participants ? (
                    <span className="text-xs text-gray-500">{t.enrolled_count}/{t.max_participants} seats</span>
                  ) : (
                    <span className="text-xs text-gray-500">{t.enrolled_count} enrolled</span>
                  )}
                </div>

                {Number(t.fee) > 0 ? (
                  <p className="text-sm font-bold text-gray-800 mb-3">Fee: ₦{Number(t.fee).toLocaleString()}</p>
                ) : (
                  <p className="text-sm font-bold text-green-600 mb-3">FREE</p>
                )}

                <button className={`w-full text-center text-sm px-4 py-2 rounded-lg font-semibold transition ${
                  Number(t.fee) > 0
                    ? 'bg-[#C8A951] text-white hover:bg-[#b89840]'
                    : 'bg-[#0B5D1E] text-white hover:bg-[#062E10]'
                }`}>
                  {Number(t.fee) > 0 ? `Pay ₦${Number(t.fee).toLocaleString()} & Enroll` : 'View Details & Enroll'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Training Detail View ───

function TrainingDetailView({ training: initialTraining, onBack }) {
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [paystackKey, setPaystackKey] = useState(null);
  const [paystackLabel, setPaystackLabel] = useState('');

  const fetchDetail = useCallback(() => {
    setLoading(true);
    api.get(`/mcpd/trainings/${initialTraining.id}/`)
      .then(r => setTraining(r.data))
      .catch(() => toast.error('Failed to load training details.'))
      .finally(() => setLoading(false));
  }, [initialTraining.id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // Fetch Paystack public key on mount
  useEffect(() => {
    api.get('/payment-config/')
      .then(r => { if (r.data.public_key) setPaystackKey(r.data.public_key); if (r.data.label) setPaystackLabel(r.data.label); })
      .catch(() => {});
  }, []);

  // Preload Paystack script on mount so it's ready instantly
  useEffect(() => { loadPaystackScript().catch(() => {}); }, []);

  const loadPaystackScript = () => {
    return new Promise((resolve, reject) => {
      if (window.PaystackPop) return resolve();
      const existing = document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]');
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.src = 'https://js.paystack.co/v1/inline.js';
      script.onload = () => {
        if (window.PaystackPop) resolve();
        else reject(new Error('Paystack script loaded but not available'));
      };
      script.onerror = () => reject(new Error('Failed to load Paystack script'));
      document.head.appendChild(script);
    });
  };

  const handleEnroll = async () => {
    const isPaid = Number(training.fee) > 0;
    setEnrolling(true);
    try {
      const res = await api.post(`/mcpd/trainings/${initialTraining.id}/enroll/`);
      const data = res.data;

      if (isPaid && data.payment_info) {
        // Paid training — open Paystack popup
        if (!paystackKey) {
          toast.error('Payment gateway is not configured. Please contact admin.');
          setEnrolling(false);
          return;
        }

        await loadPaystackScript();

        const handler = window.PaystackPop.setup({
          key: paystackKey,
          email: data.payment_info.email,
          amount: Math.round(data.payment_info.amount * 100), // kobo
          ref: data.payment_info.reference,
          currency: 'NGN',
          label: paystackLabel,
          metadata: { training_id: initialTraining.id, training_title: training.title },
          callback: (response) => {
            api.post(`/mcpd/trainings/${initialTraining.id}/verify-payment/`, {
              reference: data.payment_info.reference,
              transaction_id: response.trxref || response.reference,
            })
              .then(() => {
                toast.success('Payment successful! You are now enrolled.');
                fetchDetail();
              })
              .catch(() => {
                toast.error('Payment received but verification failed. Contact admin.');
              })
              .finally(() => setEnrolling(false));
          },
          onClose: () => {
            toast.info('Payment cancelled. You can complete payment later from "My Enrollments".');
            setEnrolling(false);
            fetchDetail();
          },
        });
        handler.openIframe();
      } else {
        // Free training — enrolled directly
        toast.success('Successfully enrolled!');
        fetchDetail();
        setEnrolling(false);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to enroll.';
      toast.error(msg);
      setEnrolling(false);
    }
  };

  const handleRetryPayment = async (enrollment) => {
    if (!paystackKey) {
      toast.error('Payment gateway is not configured. Please contact admin.');
      return;
    }

    setEnrolling(true);
    try {
      // Re-call enroll to get payment_info for existing pending enrollment
      const res = await api.post(`/mcpd/trainings/${initialTraining.id}/enroll/`);
      const data = res.data;

      if (!data.payment_info) {
        toast.error('Could not retrieve payment details.');
        setEnrolling(false);
        return;
      }

      await loadPaystackScript();

      const handler = window.PaystackPop.setup({
        key: paystackKey,
        email: data.payment_info.email,
        amount: Math.round(data.payment_info.amount * 100),
        ref: data.payment_info.reference,
        currency: 'NGN',
        label: paystackLabel,
        callback: (response) => {
          api.post(`/mcpd/trainings/${initialTraining.id}/verify-payment/`, {
            reference: data.payment_info.reference,
            transaction_id: response.trxref || response.reference,
          })
            .then(() => {
              toast.success('Payment successful! Enrollment confirmed.');
              fetchDetail();
            })
            .catch(() => {
              toast.error('Payment received but verification failed. Contact admin.');
            })
            .finally(() => setEnrolling(false));
        },
        onClose: () => {
          toast.info('Payment cancelled.');
          setEnrolling(false);
        },
      });
      handler.openIframe();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payment.');
      setEnrolling(false);
    }
  };

  if (loading || !training) {
    return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B5D1E] mx-auto"></div></div>;
  }

  const myEnrollment = training.my_enrollment;
  const isPaid = Number(training.fee) > 0;
  const hasPendingPayment = myEnrollment && myEnrollment.payment_status === 'pending';

  return (
    <div>
      <button onClick={onBack} className="text-sm text-[#0B5D1E] font-semibold hover:underline mb-4 flex items-center gap-1">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Back to Trainings
      </button>

      {/* Header Card */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h2 className="text-xl font-bold text-gray-900">{training.title}</h2>
              <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${STATUS_COLORS[training.status]}`}>{training.status_display}</span>
              <span className={`text-xs px-2.5 py-0.5 rounded font-semibold ${LEVEL_COLORS[training.level]}`}>{training.level_display}</span>
            </div>
            <p className="text-sm text-gray-500 mb-2">{training.category_display}</p>
            {training.description && <p className="text-sm text-gray-600 mb-3">{training.description}</p>}
            <div className="space-y-1.5 text-sm text-gray-600">
              <p><strong>Dates:</strong> {training.date_from} — {training.date_to}
                {training.time_start && ` | ${training.time_start} – ${training.time_end}`}
              </p>
              {training.instructor_name && (
                <p><strong>Instructor:</strong> {training.instructor_name}</p>
              )}
              {training.instructor_bio && <p className="text-xs text-gray-400 italic ml-4">{training.instructor_bio}</p>}
              {(training.venue || training.is_virtual) && (
                <p><strong>Venue:</strong> {training.is_virtual ? 'Virtual / Online' : training.venue}
                  {training.is_virtual && training.meeting_link && (
                    <> — <a href={training.meeting_link} target="_blank" rel="noopener noreferrer" className="text-[#0B5D1E] hover:underline">Join Meeting</a></>
                  )}
                </p>
              )}
            </div>
            {training.training_file && (
              <div className="mt-3">
                <a href={training.training_file} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm bg-[#0B5D1E] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#062E10] transition">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  Download Training File
                </a>
              </div>
            )}
          </div>
          <div className="text-left md:text-right space-y-2 flex-shrink-0">
            <p className="text-3xl font-bold text-[#0B5D1E]">{training.cpd_points} <span className="text-base">CPD pts</span></p>
            <p className="text-sm text-gray-500">
              {training.enrolled_count}{training.max_participants ? `/${training.max_participants}` : ''} enrolled
            </p>
            {Number(training.fee) > 0 && <p className="text-lg font-bold text-gray-800">₦{Number(training.fee).toLocaleString()}</p>}

            {myEnrollment ? (
              <div className="mt-3">
                {hasPendingPayment ? (
                  <>
                    <span className="inline-block text-sm px-4 py-2 rounded-lg font-semibold bg-amber-100 text-amber-700 mb-2">
                      Payment Pending
                    </span>
                    <p className="text-xs text-gray-500 mb-2">Enrolled but payment not completed.</p>
                    <button onClick={() => handleRetryPayment(myEnrollment)} disabled={enrolling}
                      className="px-6 py-2.5 rounded-lg text-sm font-semibold bg-[#C8A951] text-white hover:bg-[#b89840] transition disabled:opacity-50">
                      {enrolling ? 'Processing...' : `Complete Payment — ₦${Number(training.fee).toLocaleString()}`}
                    </button>
                  </>
                ) : (
                  <>
                    <span className={`inline-block text-sm px-4 py-2 rounded-lg font-semibold ${
                      myEnrollment.status === 'completed' ? 'bg-green-100 text-green-700' :
                      myEnrollment.status === 'enrolled' ? 'bg-blue-100 text-blue-700' :
                      myEnrollment.status === 'waitlisted' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {myEnrollment.status === 'completed' ? '✓ Completed' :
                       myEnrollment.status === 'enrolled' ? '✓ Enrolled' :
                       myEnrollment.status === 'waitlisted' ? 'Waitlisted' :
                       myEnrollment.status_display}
                    </span>
                    {myEnrollment.payment_status === 'paid' && (
                      <p className="text-xs text-green-600 mt-1 font-semibold">Payment Confirmed</p>
                    )}
                  </>
                )}
                {myEnrollment.cpd_awarded > 0 && (
                  <p className="text-xs text-[#0B5D1E] mt-1 font-semibold">{myEnrollment.cpd_awarded} CPD Awarded</p>
                )}
                {myEnrollment.certificate_issued && (
                  <p className="text-xs text-purple-600 mt-1 font-semibold">Certificate Issued</p>
                )}
              </div>
            ) : (
              <button onClick={handleEnroll} disabled={enrolling || training.is_full}
                className={`mt-3 px-6 py-2.5 rounded-lg text-sm font-semibold transition ${
                  training.is_full
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : isPaid ? 'bg-[#C8A951] text-white hover:bg-[#b89840]'
                    : 'bg-[#0B5D1E] text-white hover:bg-[#062E10]'
                }`}>
                {enrolling ? 'Processing...' :
                 training.is_full ? 'Training Full' :
                 isPaid ? `Pay ₦${Number(training.fee).toLocaleString()} & Enroll` :
                 'Enroll Now (Free)'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modules */}
      {training.modules && training.modules.length > 0 && (
        <div className="mb-6">
          <h3 className="text-md font-bold text-gray-800 mb-3">Training Modules ({training.modules.length})</h3>
          <div className="space-y-2">
            {training.modules.map(mod => (
              <div key={mod.id} className={`bg-white rounded-xl border p-4 flex items-start gap-3 ${mod.is_completed ? 'border-green-200 bg-green-50' : ''}`}>
                <div className={`mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  mod.is_completed ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300'
                }`}>
                  {mod.is_completed && <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">#{mod.order}</span>
                    <h4 className={`font-semibold text-gray-900 text-sm ${mod.is_completed ? 'line-through text-gray-500' : ''}`}>{mod.title}</h4>
                  </div>
                  {mod.description && <p className="text-xs text-gray-500">{mod.description}</p>}
                  <div className="flex gap-3 text-xs text-gray-400 mt-1">
                    <span>{mod.duration_minutes} min</span>
                    {mod.date && <span>{mod.date}</span>}
                    {mod.time_start && <span>{mod.time_start}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resources */}
      {training.resources && training.resources.length > 0 && (
        <div>
          <h3 className="text-md font-bold text-gray-800 mb-3">Resources & Materials ({training.resources.length})</h3>
          <div className="space-y-2">
            {training.resources.map(res => {
              const icons = { document: '📄', video: '🎥', link: '🔗', slide: '📊' };
              return (
                <div key={res.id} className="bg-white rounded-xl border p-4 flex items-center gap-4">
                  <span className="text-2xl">{icons[res.resource_type] || '📁'}</span>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 text-sm">{res.title}</h4>
                    <p className="text-xs text-gray-500 capitalize">{res.resource_type_display}</p>
                  </div>
                  {(res.file || res.external_url) && (
                    <a href={res.file || res.external_url} target="_blank" rel="noopener noreferrer"
                      className="text-xs bg-[#0B5D1E] text-white px-3 py-1.5 rounded-lg font-semibold hover:bg-[#062E10] transition">
                      Open
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// ═══ My Enrollments Tab ═══

function MyEnrollmentsTab() {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/mcpd/trainings/my/')
      .then(r => setEnrollments(r.data.results || r.data))
      .catch(() => toast.error('Failed to load enrollments.'))
      .finally(() => setLoading(false));
  }, []);

  const ENROLLMENT_COLORS = {
    enrolled: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    dropped: 'bg-red-100 text-red-700',
    waitlisted: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <div>
      {/* Search */}
      <div className="relative mb-4">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        <input type="text" placeholder="Search enrollments..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent" />
      </div>
      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B5D1E] mx-auto"></div></div>
      ) : enrollments.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
          <p className="text-gray-500 font-semibold">You haven't enrolled in any trainings yet.</p>
          <p className="text-sm text-gray-400 mt-1">Browse "Available Trainings" to get started!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {enrollments.filter(en => !search || en.training_title.toLowerCase().includes(search.toLowerCase())).map((en, idx) => (
            <div key={en.id} className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <span className="bg-[#0B5D1E] text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                    <h3 className="font-bold text-gray-900">{en.training_title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${ENROLLMENT_COLORS[en.status]}`}>{en.status_display}</span>
                    {en.payment_status === 'pending' && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-700">Payment Pending</span>
                    )}
                    {en.payment_status === 'paid' && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-green-100 text-green-600">Paid</span>
                    )}
                  </div>
                  <div className="flex gap-4 text-sm text-gray-500">
                    <span>Enrolled: {new Date(en.enrolled_at).toLocaleDateString()}</span>
                    {en.attendance_marked && <span className="text-green-600 font-semibold">Attendance Marked</span>}
                  </div>
                </div>
                <div className="text-left sm:text-right">
                  {en.cpd_awarded > 0 ? (
                    <p className="text-xl font-bold text-[#0B5D1E]">{en.cpd_awarded} CPD</p>
                  ) : (
                    <p className="text-sm text-gray-400">CPD Pending</p>
                  )}
                  {en.certificate_issued && (
                    <p className="text-xs text-purple-600 font-semibold mt-1">Certificate Issued</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ═══ My CPD Records Tab (existing submit & review) ═══

function MyCPDRecordsTab({ records, setRecords, loading }) {
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    activity_type: 'conference', title: '', description: '', organizer: '',
    date_from: '', date_to: '', cpd_points: '', year: new Date().getFullYear(),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/mcpd/submit/', formData);
      setRecords([res.data, ...records]);
      setShowForm(false);
      setFormData({ activity_type: 'conference', title: '', description: '', organizer: '', date_from: '', date_to: '', cpd_points: '', year: new Date().getFullYear() });
      toast.success('MCPD record submitted for review!');
    } catch {
      toast.error('Failed to submit record.');
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowForm(!showForm)}
          className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition flex items-center gap-2">
          {showForm ? 'Cancel' : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Submit Activity
            </>
          )}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border p-6 mb-6 space-y-4">
          <h3 className="font-bold text-gray-900">Submit CPD Activity</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Activity Type</label>
              <select value={formData.activity_type} onChange={(e) => setFormData({ ...formData, activity_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                <option value="conference">Conference/Seminar</option>
                <option value="workshop">Workshop/Training</option>
                <option value="publication">Publication</option>
                <option value="fieldwork">Fieldwork</option>
                <option value="teaching">Teaching/Mentoring</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Title *</label>
              <input type="text" required value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Organizer</label>
              <input type="text" value={formData.organizer}
                onChange={(e) => setFormData({ ...formData, organizer: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">CPD Points *</label>
              <input type="number" required value={formData.cpd_points}
                onChange={(e) => setFormData({ ...formData, cpd_points: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Date From *</label>
              <input type="date" required value={formData.date_from}
                onChange={(e) => setFormData({ ...formData, date_from: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Date To *</label>
              <input type="date" required value={formData.date_to}
                onChange={(e) => setFormData({ ...formData, date_to: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
            <textarea value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" rows={3} />
          </div>
          <button type="submit" className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg font-semibold text-sm hover:bg-[#062E10] transition">Submit</button>
        </form>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        <input type="text" placeholder="Search records..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent" />
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B5D1E] mx-auto"></div></div>
        ) : records.length === 0 ? (
          <div className="bg-white rounded-xl border p-8 text-center text-gray-400 text-sm">No CPD records yet. Submit your first activity above.</div>
        ) : records.filter(r => !search || r.title.toLowerCase().includes(search.toLowerCase()) || (r.organizer || '').toLowerCase().includes(search.toLowerCase()) || (r.activity_type || '').toLowerCase().includes(search.toLowerCase())).map((r, idx) => (
          <div key={r.id} className="bg-white rounded-xl shadow-sm border p-5">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <span className="bg-[#0B5D1E] text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                  <h3 className="font-semibold text-gray-900">{r.title}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    r.status === 'approved' ? 'bg-green-100 text-green-700' :
                    r.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>{r.status}</span>
                </div>
                <p className="text-sm text-gray-500 capitalize">{r.activity_type?.replace('_', ' ')} | {r.organizer}</p>
                <p className="text-sm text-gray-500">{r.date_from} to {r.date_to}</p>
                {r.description && <p className="text-sm text-gray-600 mt-1">{r.description}</p>}
                {r.reviewer_comment && <p className="text-sm text-blue-600 mt-1 italic">Review: {r.reviewer_comment}</p>}
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-[#0B5D1E]">{r.cpd_points} pts</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
